import React, { useState } from "react";
import {
  Card,
  Select,
  Button,
  Space,
  Typography,
  Row,
  Col,
  List,
  Tag,
  Modal,
  Input,
  Popconfirm,
  Empty,
  Spin,
  Tooltip,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import useSkillsInventory from "PlatForms/OrgLMS/Hooks/AdminHooks/useSkillsInventory";

const { Text } = Typography;
const { Option } = Select;

const SkillsInventory = () => {
  const {
    businessGroups,
    selectedGroupId,
    setSelectedGroupId,
    skills,
    loadingGroups,
    loadingSkills,
    saving,
    addSkills,
    editSkill,
    removeSkill,
  } = useSkillsInventory();

  const [newSkills, setNewSkills] = useState([]);
  const [editing, setEditing] = useState(null); // { id, name }
  const [editName, setEditName] = useState("");

  const handleAdd = async () => {
    const ok = await addSkills(newSkills);
    if (ok) setNewSkills([]);
  };

  const openEdit = (skill) => {
    setEditing(skill);
    setEditName(skill.name);
  };

  const handleEditSave = async () => {
    const ok = await editSkill(editing.id, editName);
    if (ok) setEditing(null);
  };

  return (
    <Card
      className="card-professional"
      title="Skills Inventory"
      loading={loadingGroups}
    >
      <Text type="secondary" style={{ display: "block", marginBottom: 20 }}>
        Manage the skills available to each business group. Skills are shared
        across courses, learners and mentor profiles.
      </Text>

      <Row gutter={[16, 16]} align="bottom">
        <Col xs={24} md={8}>
          <Text strong style={{ display: "block", marginBottom: 6 }}>
            Business Group
          </Text>
          <Select
            style={{ width: "100%" }}
            placeholder="Select a business group"
            value={selectedGroupId ?? undefined}
            onChange={setSelectedGroupId}
            showSearch
            optionFilterProp="children"
          >
            {businessGroups.map((g) => (
              <Option key={g.id} value={g.id}>
                {g.name}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} md={12}>
          <Text strong style={{ display: "block", marginBottom: 6 }}>
            Add Skills
          </Text>
          <Select
            mode="tags"
            style={{ width: "100%" }}
            placeholder="Type a skill and press Enter (e.g. React, SQL)"
            value={newSkills}
            onChange={setNewSkills}
            tokenSeparators={[","]}
            open={false}
            disabled={!selectedGroupId}
          />
        </Col>
        <Col xs={24} md={4}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            block
            loading={saving}
            disabled={!selectedGroupId || newSkills.length === 0}
            onClick={handleAdd}
          >
            Add
          </Button>
        </Col>
      </Row>

      <div style={{ marginTop: 28 }}>
        <Text strong style={{ display: "block", marginBottom: 12 }}>
          Skills in this group{" "}
          <Tag color="gold">{skills.length}</Tag>
        </Text>

        {loadingSkills ? (
          <div style={{ textAlign: "center", padding: "32px 0" }}>
            <Spin />
          </div>
        ) : skills.length === 0 ? (
          <Empty description="No skills yet for this business group. Add some above." />
        ) : (
          <List
            bordered
            dataSource={skills}
            style={{ background: "#FFFEFB" }}
            renderItem={(skill) => {
              const deletable = skill.isDeletable !== false;
              const usedBy = [
                skill.isUsedByLearners && "learners",
                skill.isUsedByCourses && "courses",
                skill.isUsedByMentors && "mentors",
              ]
                .filter(Boolean)
                .join(", ");
              return (
                <List.Item
                  actions={[
                    <Button
                      key="edit"
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => openEdit(skill)}
                    >
                      Edit
                    </Button>,
                    deletable ? (
                      <Popconfirm
                        key="delete"
                        title="Remove this skill?"
                        description="It will no longer be selectable for this business group."
                        okText="Remove"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => removeSkill(skill.id)}
                      >
                        <Button type="text" danger icon={<DeleteOutlined />}>
                          Remove
                        </Button>
                      </Popconfirm>
                    ) : (
                      <Tooltip
                        key="delete"
                        title={`In use${usedBy ? ` by ${usedBy}` : ""}, so it can't be removed`}
                      >
                        <Button type="text" danger icon={<DeleteOutlined />} disabled>
                          Remove
                        </Button>
                      </Tooltip>
                    ),
                  ]}
                >
                  <Space>
                    <Tag color="gold" style={{ fontSize: 14, padding: "2px 10px" }}>
                      {skill.name}
                    </Tag>
                    {!deletable && usedBy && (
                      <Tag color="default">in use by {usedBy}</Tag>
                    )}
                  </Space>
                </List.Item>
              );
            }}
          />
        )}
      </div>

      <Modal
        title="Edit Skill"
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={handleEditSave}
        okText="Save"
        confirmLoading={saving}
        okButtonProps={{ disabled: !editName.trim() }}
      >
        <Input
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          placeholder="Skill name"
          onPressEnter={handleEditSave}
        />
      </Modal>
    </Card>
  );
};

export default SkillsInventory;
